import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  Group,
  Loader,
  Paper,
  Select,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { IconDownload, IconSearch } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import useTransientStore from "../../data/transcientStore";
import { usePermissions } from "../../hooks/usePermissions";
import { useTranslation } from "../../hooks/useTranslation";
import { getContractsByAddressSystemAndUser } from "../../services/MapComponentsService";
import {
  exportYearReportData,
  postYearReportSummary,
} from "../../services/CityObjectYearReportService";
import {
  CityObjectYearReportDetailRow,
  CityObjectYearReportByContractRow,
  CityObjectYearReportByItemRow,
} from "../../types/CityObjectYearReport";
import { ContractListItem } from "../../types/UserRole";
import classes from "./styles.module.css";

const EXCEL_MIME =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

function pickErrorFieldMessage(value: unknown): string | null {
  if (!value || typeof value !== "object") return null;
  const obj = value as { message?: string; Message?: string; title?: string };

  if (typeof obj.message === "string" && obj.message) return obj.message;
  if (typeof obj.Message === "string" && obj.Message) return obj.Message;
  if (typeof obj.title === "string" && obj.title) return obj.title;

  return null;
}

function getApiErrorMessage(e: unknown, fallback = "Request failed"): string {
  if (typeof e === "string" && e.trim()) return e;
  if (!e || typeof e !== "object") return fallback;

  const resp = (e as { response?: { data?: unknown } }).response;
  const data = resp?.data;

  if (typeof data === "string" && data.trim()) return data;

  const objectMessage = pickErrorFieldMessage(data);
  if (objectMessage) return objectMessage;

  if (e instanceof Error && e.message) return e.message;
  return fallback;
}

export default function CityObjectYearReportPage() {
  const selectedAddressSystem = useTransientStore(
    (state) => state.selectedAddressSystem,
  );
  const currentUserId = useTransientStore((state) => state.currentUserId);
  const { checkAccess } = usePermissions();
  const canShow = checkAccess("CITY_OBJECT_SHOW");

  const addrId = selectedAddressSystem?.id || 0;
  const { getTranslation } = useTranslation({
    pageId: 515,
    addressSystemId: addrId || 0,
    autoLoad: true,
  });

  const [contracts, setContracts] = useState<ContractListItem[]>([]);
  const [contractStatus, setContractStatus] = useState<"active" | "archived">(
    "active",
  );
  const [selectedContractIds, setSelectedContractIds] = useState<number[]>([]);
  const [dateFrom, setDateFrom] = useState<Date | null>(null);
  const [dateTo, setDateTo] = useState<Date | null>(null);
  const [loadingData, setLoadingData] = useState(true);
  const [activeAction, setActiveAction] = useState<string | null>(null);
  const [rowCount, setRowCount] = useState<number | null>(null);

  const fmt = (d: Date | null) =>
    d
      ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`
      : null;

  useEffect(() => {
    const load = async () => {
      if (!addrId || !currentUserId) {
        setLoadingData(false);
        return;
      }
      try {
        setLoadingData(true);
        const cRowsRaw = await getContractsByAddressSystemAndUser(
          addrId,
          currentUserId,
        );
        const cRows = cRowsRaw as ContractListItem[];
        setContracts(cRows);
      } catch (e) {
        console.error(e);
        notifications.show({
          title: getTranslation("YEAR_REPORT_LOAD_FAILED") || "Load failed",
          message: getApiErrorMessage(
            e,
            getTranslation("YEAR_REPORT_REQUEST_FAILED") || "Request failed",
          ),
          color: "red",
        });
      } finally {
        setLoadingData(false);
      }
    };
    void load();
  }, [addrId, currentUserId]);

  const filteredContracts = useMemo(() => {
    return contracts.filter((contract) => {
      if (contractStatus === "active") {
        return contract.contractStatusDefaultValue === 1;
      } else {
        return contract.contractStatusDefaultValue !== 1;
      }
    });
  }, [contracts, contractStatus]);

  useEffect(() => {
    if (filteredContracts.length === 0) {
      setSelectedContractIds([]);
      return;
    }
    setSelectedContractIds(filteredContracts.map((c) => c.id));
  }, [filteredContracts]);

  const buildRequestBase = useCallback(() => {
    if (!addrId) throw new Error("No address system");
    return {
      addressSystemId: addrId,
      currentUserId: currentUserId ?? 0,
      contractIds: selectedContractIds,
      contractStatusId: null,
      approvedDateFrom: fmt(dateFrom),
      approvedDateTo: fmt(dateTo),
    };
  }, [addrId, currentUserId, selectedContractIds, dateFrom, dateTo]);

  const toggleContract = (id: number, checked: boolean) => {
    setSelectedContractIds((prev) => {
      if (checked) return prev.includes(id) ? prev : [...prev, id];
      return prev.filter((x) => x !== id);
    });
  };

  const selectAllContracts = () => {
    setSelectedContractIds(filteredContracts.map((c) => c.id));
  };

  const clearContracts = () => {
    setSelectedContractIds([]);
  };

  const handleRun = async () => {
    if (selectedContractIds.length === 0) {
      notifications.show({
        title: getTranslation("YEAR_REPORT_NO_CONTRACTS") || "Contracts",
        message:
          getTranslation("YEAR_REPORT_NO_CONTRACTS_MSG") ||
          "Select at least one contract.",
        color: "yellow",
      });
      return;
    }
    try {
      setActiveAction("run");
      setRowCount(null);
      const summary = await postYearReportSummary(buildRequestBase());
      setRowCount(summary.rowCount);
    } catch (e) {
      console.error(e);
      notifications.show({
        title: getTranslation("YEAR_REPORT_RUN_ERROR") || "Report failed",
        message: getApiErrorMessage(
          e,
          getTranslation("YEAR_REPORT_REQUEST_FAILED") || "Request failed",
        ),
        color: "red",
      });
    } finally {
      setActiveAction(null);
    }
  };

  const handleExport = async (exportKind: 0 | 1 | 2, filename: string) => {
    if (selectedContractIds.length === 0) {
      notifications.show({
        title: getTranslation("YEAR_REPORT_NO_CONTRACTS") || "Contracts",
        message:
          getTranslation("YEAR_REPORT_NO_CONTRACTS_MSG") ||
          "Select at least one contract.",
        color: "yellow",
      });
      return;
    }
    try {
      setActiveAction(`export-${exportKind}`);
      const data = await exportYearReportData({
        ...buildRequestBase(),
        exportKind,
      });
      
      // Generate Excel file
      const workbook = XLSX.utils.book_new();
      let sheetData: unknown[][];
      
      if (exportKind === 0) {
        // Detail export
        const detailData = data as CityObjectYearReportDetailRow[];
        const headers = [
          "Urakka", "Tilaaja", "Organisaatio 1", "Organisaatio 2", "Viite", "Kohde", "Tyyppi", "Tila", "Valmistunut PVM",
          "Hyväksytty PVM", "Ryhmä", "Laji", "Yksikkö", "Määrä", "Massa", "Pinta-ala", "Yksikköhinta", "Yhteensä", "Kohde yhteensä", "ID"
        ];
        sheetData = [
          headers,
          ...detailData.map(r => [
            r.urakka || "", r.tilaaja || "", r.organization1 || "", r.organization2 || "", r.invoiceReference || "",
            r.location || "", r.tyyppi || "", r.tila || "", r.valmistunutPvm || "", r.hyvaksyttyPvm || "",
            r.gName || "", r.itemName || "", r.unitName || "", r.itemCount || "", r.itemWeight || "", r.itemArea || "",
            r.unitPrice || "", r.rowPrice || "", r.objectTotal || "", r.id
          ])
        ];
      } else if (exportKind === 1) {
        // By contract export
        const contractData = data as CityObjectYearReportByContractRow[];
        const headers = ["Urakka", "Ryhmä", "Laji", "Yksikkö", "Määrä", "Massa", "Pinta-ala", "Yksikköhinta", "Yhteensä"];
        sheetData = [
          headers,
          ...contractData.map(r => [
            r.urakka || "", r.gName || "", r.itemName || "", r.unitName || "",
            r.itemCount || "", r.itemWeight || "", r.itemArea || "", r.unitPrice || "", r.rowPrice || ""
          ])
        ];
      } else {
        // By item export
        const itemData = data as CityObjectYearReportByItemRow[];
        const headers = ["Ryhmä", "Laji", "Yksikkö", "Määrä", "Massa", "Pinta-ala", "Yksikköhinta", "Yhteensä"];
        sheetData = [
          headers,
          ...itemData.map(r => [
            r.gName || "", r.itemName || "", r.unitName || "",
            r.itemCount || "", r.itemWeight || "", r.itemArea || "", r.unitPrice || "", r.rowPrice || ""
          ])
        ];
      }
      
      const worksheet = XLSX.utils.aoa_to_sheet(sheetData);
      XLSX.utils.book_append_sheet(workbook, worksheet, "Report");
      
      const workbookArray = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });
      const blob = new Blob([workbookArray], { type: EXCEL_MIME });
      saveAs(blob, filename);
      
    } catch (e) {
      console.error(e);
      const requestFailed =
        getTranslation("YEAR_REPORT_REQUEST_FAILED") || "Request failed";
      const message = getApiErrorMessage(e, requestFailed);
      notifications.show({
        title: getTranslation("YEAR_REPORT_EXPORT_ERROR") || "Export failed",
        message,
        color: "red",
      });
    } finally {
      setActiveAction(null);
    }
  };

  if (!canShow) {
    return (
      <Paper className={classes.wrap} withBorder>
        <Text c="dimmed">
          {getTranslation("YEAR_REPORT_NO_ACCESS") ||
            "You do not have access to this report."}
        </Text>
      </Paper>
    );
  }

  if (loadingData) {
    return (
      <Group className={classes.wrap} justify="center">
        <Loader />
      </Group>
    );
  }

  return (
    <Stack className={classes.wrap} gap="md">
      <Title order={3} className={classes.title}>
        {getTranslation("YEAR_REPORT_TITLE") || "City object year report"}
      </Title>
      <Text size="sm" c="dimmed">
        {getTranslation("YEAR_REPORT_HINT") ||
          "Filter by approved date (Hyväksytty PVM), choose contracts, then run the report or download CSV exports."}
      </Text>

      <Select
        label={
          getTranslation("YEAR_REPORT_CONTRACT_STATUS") || "Contract Status"
        }
        value={contractStatus}
        onChange={(value) =>
          setContractStatus(value as "active" | "archived")
        }
        data={[
          {
            value: "active",
            label:
              getTranslation("YEAR_REPORT_ACTIVE_CONTRACTS") ||
              "Active Contracts",
          },
          {
            value: "archived",
            label:
              getTranslation("YEAR_REPORT_ARCHIVED_CONTRACTS") ||
              "Archived Contracts",
          },
        ]}
        placeholder={
          getTranslation("YEAR_REPORT_SELECT_CONTRACT_STATUS") ||
          "Select contract status"
        }
      />

      <div>
        <Group justify="space-between" mb="xs">
          <Text fw={600}>
            {getTranslation("YEAR_REPORT_CONTRACTS") || "Contracts"}
          </Text>
          <Group gap="xs">
            <Button size="xs" variant="light" onClick={selectAllContracts}>
              {getTranslation("YEAR_REPORT_SELECT_ALL") || "Select all"}
            </Button>
            <Button size="xs" variant="light" onClick={clearContracts}>
              {getTranslation("YEAR_REPORT_CLEAR") || "Clear"}
            </Button>
          </Group>
        </Group>
        <Stack gap="xs">
          {filteredContracts.map((c) => (
            <Checkbox
              key={c.id}
              label={c.name}
              checked={selectedContractIds.includes(c.id)}
              onChange={(ev) => toggleContract(c.id, ev.currentTarget.checked)}
            />
          ))}
        </Stack>
      </div>

      <Group grow>
        <DatePickerInput
          label={getTranslation("YEAR_REPORT_DATE_FROM") || "Approved from"}
          value={dateFrom}
          onChange={setDateFrom}
          clearable
          valueFormat="YYYY-MM-DD"
        />
        <DatePickerInput
          label={getTranslation("YEAR_REPORT_DATE_TO") || "Approved to"}
          value={dateTo}
          onChange={setDateTo}
          clearable
          valueFormat="YYYY-MM-DD"
        />
      </Group>

      <Group>
        <Button
          leftSection={<IconSearch size={18} />}
          onClick={() => void handleRun()}
          loading={activeAction === "run"}
        >
          {getTranslation("YEAR_REPORT_RUN") || "Run report"}
        </Button>
        {rowCount !== null && (
          <Text>
            {(getTranslation("YEAR_REPORT_ROWS") || "{n} rows").replace(
              "{n}",
              String(rowCount),
            )}
          </Text>
        )}
      </Group>

      {rowCount !== null && rowCount > 0 && (
        <Stack gap="sm">
          <Text fw={600}>
            {getTranslation("YEAR_REPORT_DOWNLOADS") || "Downloads"}
          </Text>
          <Group>
            <Button
              variant="light"
              leftSection={<IconDownload size={18} />}
              onClick={() =>
                void handleExport(0, "city_object_year_report_detail.xlsx")
              }
              loading={activeAction === "export-0"}
            >
              {getTranslation("YEAR_REPORT_EXCEL_DETAIL") || "Detail Excel"}
            </Button>
            <Button
              variant="light"
              leftSection={<IconDownload size={18} />}
              onClick={() =>
                void handleExport(1, "city_object_year_report_by_contract.xlsx")
              }
              loading={activeAction === "export-1"}
            >
              {getTranslation("YEAR_REPORT_CSV_BY_CONTRACT") ||
                "Summary by contract"}
            </Button>
            <Button
              variant="light"
              leftSection={<IconDownload size={18} />}
              onClick={() =>
                void handleExport(2, "city_object_year_report_by_item.xlsx")
              }
              loading={activeAction === "export-2"}
            >
              {getTranslation("YEAR_REPORT_CSV_BY_ITEM") || "Summary by item"}
            </Button>
          </Group>
        </Stack>
      )}
    </Stack>
  );
}
